import type { ContentType } from "@brookbush/database";
import type { Comment } from "@brookbush/types";

import { useMemo } from "react";
import useSWRInfinite from "swr/infinite";

import fetcher from "~/utils/fetcher.client";

interface UseCommentsProps {
	contentId: string;
	contentType: ContentType;
}

const useComments = (props?: UseCommentsProps) => {
	const { mutate, data, size, setSize, ...rest } = useSWRInfinite<{
		comments: Comment[];
		totalCount: number;
	}>(
		(pageIndex, previousPageData) => {
			if (!props || (previousPageData && !hasMore)) {
				return null;
			}

			return `/comments?contentId=${props.contentId}&contentType=${
				props.contentType
			}&page=${pageIndex + 1}`;
		},
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
		},
	);

	const comments = useMemo(
		() =>
			data
				? data
						.flat()
						.reduce(
							(prev: Comment[], curr) => prev.concat(...curr.comments),
							[],
						)
				: undefined,
		[data],
	);

	const hasMore = data && comments!.length < data?.[data.length - 1].totalCount;

	const addComment = async (body: string, parentComment?: Comment) => {
		await mutate(
			fetcher(`/comments`, {
				method: "POST",
				data: {
					contentId: props?.contentId,
					contentType: props?.contentType,
					replyToId: parentComment?.id,
					body,
				},
			}),

			{
				populateCache: false,
			},
		);
	};

	const removeComment = async (id: number) => {
		await mutate(
			fetcher(`/comments/${id}`, {
				method: "DELETE",
			}),
			{
				populateCache: false,
				revalidate: true,
			},
		);
	};

	const updateComment = async (id: number, body: string) => {
		await mutate(
			fetcher(`/comments/${id}`, {
				method: "PATCH",
				data: {
					body,
				},
			}),
			{
				populateCache: false,
				revalidate: true,
			},
		);
	};

	return {
		comments,
		hasMore,
		addComment,
		removeComment,
		updateComment,
		size,
		setSize,
		...rest,
	};
};

export default useComments;
